<script lang="ts">
  import type { HTMLInputAttributes } from 'svelte/elements'
  import { Lock, LockOpen } from '@lucide/svelte'
  import IconButton from '../icon-button/IconButton.svelte'

  type TranslationInputProps = Omit<HTMLInputAttributes, 'onchange'> & {
    label?: string
    unit?: string
    isLockable?: boolean
    isLocked?: boolean
    hasMultipleValues?: boolean
    isMixed?: boolean
    onLockChange?: (newValue: boolean) => void
    ontype?: (value: number) => void
    onarrowpressed?: (value: number) => void
  }

  let {
    label,
    isLockable = false,
    hasMultipleValues = false,
    isMixed = false,
    isLocked = false,
    onLockChange,
    defaultValue = 0,
    ontype,
    onarrowpressed,
    ...inputProps
  }: TranslationInputProps = $props()

  let inputRef: HTMLInputElement
  let inputValue = $derived<number | string>(isMixed ? 'Mixed' : defaultValue)

  function handleKeyPress(e: KeyboardEvent): void {
    if (hasMultipleValues || !onarrowpressed || typeof inputValue !== 'number')
      return

    const step = e.shiftKey ? 100 : 1

    if (e.key === 'ArrowUp') {
      const newValue = inputValue + step
      onarrowpressed(newValue)
      inputValue = newValue
    }
    else if (e.key === 'ArrowDown') {
      const newValue = inputValue - step
      onarrowpressed(newValue)
      inputValue = newValue
    }
  }

  function handleType(e: Event & { currentTarget: HTMLInputElement }) {
    e.preventDefault()

    if (isLocked) return

    const newValue = parseFloat(e.currentTarget.value)

    if (Number.isNaN(newValue)) {
      if (inputRef) {
        inputRef.value = String(inputValue)
      }
      return
    }

    inputValue = newValue
    ontype?.(newValue)
  }
</script>

<div
  class="container"
  aria-labelledby="input-label"
  role="group"
>
  <div>
    <p id="input-label">{label}</p>
    <input
      {...inputProps}
      bind:this={inputRef}
      class:nopadding={!label}
      aria-label={label}
      aria-readonly={isLocked}
      disabled={isLocked}
      onchange={handleType}
      onkeydown={handleKeyPress}
      readonly={isLocked}
      type="text"
      value={inputValue}
    />
  </div>
  {#if isLockable}
    <IconButton
      aria-label={isLocked ? 'Unlock input' : 'Lock input'}
      onclick={() => onLockChange?.(!isLocked)}
      size={1.75}
    >
      {#if isLocked}
        <Lock />
      {:else}
        <LockOpen />
      {/if}
    </IconButton>
  {/if}
</div>

<style lang="scss">
  .container {
    width: 100%;
    display: flex;
    background-color: var(--translation-input-background);
    border-radius: 0.25rem;
    border: 1px solid var(--translation-input-background);
    min-height: 1.75rem;

    :global(button) {
      color: var(--translation-input-icon);
    }

    & > div {
      position: relative;
      width: 100%;
      display: flex;
      font-size: 0.75rem;
      line-height: 100%;

      & > * {
        font: inherit;
      }

      & > input {
        caret-color: var(--translation-input-foreground);
        color: var(--translation-input-foreground);
        border: none;
        background-color: transparent;
        width: 100%;
        outline: none;
        margin: 0;
        padding-left: 1.75rem;
        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        &.nopadding {
          padding-left: 0.5rem;
        }
      }

      // span {
      //   position: absolute;
      //   left: 1.75rem;
      //   right: 0;
      //   top: 50%;
      //   transform: translateY(-50%);
      //   pointer-events: none;
      //   overflow: hidden;
      // }

      & > p {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        place-content: center;
        text-align: center;
        pointer-events: none;
        min-width: 1.75rem;
        color: var(--right-bar-muted-foreground);
      }
    }

    &:hover {
      border-color: var(--translation-input-border);
    }

    &:has(div input:focus-visible) {
      border-color: var(--translation-input-hover-border);
    }
  }
</style>
